import { useState } from 'react';
import { SpinnerWheel } from './SpinnerWheel';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Plus, Minus, RotateCcw } from 'lucide-react';

export const CustomWheelBuilder = ({ availableItems, onSpinComplete, category }) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [showWheel, setShowWheel] = useState(false);

  const addItem = (item) => {
    if (!selectedItems.find(i => i.id === item.id)) {
      setSelectedItems([...selectedItems, item]);
    }
  };

  const removeItem = (itemId) => {
    setSelectedItems(selectedItems.filter(i => i.id !== itemId));
  };

  const resetSelection = () => {
    setSelectedItems([]);
    setShowWheel(false);
  };

  const handleSpinComplete = (item) => {
    onSpinComplete(item);
  };

  return (
    <div className="w-full" data-testid="custom-wheel-builder">
      {!showWheel ? (
        <div className="grid md:grid-cols-2 gap-8">
          {/* Available Items */}
          <div className="bg-white/60 backdrop-blur-md rounded-3xl p-6 border border-white/40">
            <h4 className="font-['Playfair_Display'] text-xl font-semibold text-[#1C1917] mb-4">
              Available {category} Dishes
            </h4>
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-2">
                {availableItems.map((item) => {
                  const isSelected = selectedItems.find(i => i.id === item.id);
                  return (
                    <div
                      key={item.id}
                      className={`flex items-center justify-between p-3 rounded-xl transition-all duration-300 ${
                        isSelected 
                          ? 'bg-[#F97316]/10 border border-[#F97316]/30' 
                          : 'bg-white/50 border border-transparent hover:bg-white/80'
                      }`}
                    >
                      <div className="flex-1">
                        <p className="font-['Outfit'] font-medium text-[#1C1917]">{item.name}</p>
                        <p className="text-xs text-[#1C1917]/60 font-['Outfit']">{item.calories} cal • {item.prep_time}</p>
                      </div>
                      <button
                        onClick={() => isSelected ? removeItem(item.id) : addItem(item)}
                        data-testid={`toggle-item-${item.id}`}
                        className={`p-2 rounded-full transition-all duration-300 ${
                          isSelected 
                            ? 'bg-[#F97316] text-white hover:bg-[#EA580C]' 
                            : 'bg-[#2D4A22] text-white hover:bg-[#1C1917]'
                        }`}
                      >
                        {isSelected ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                      </button>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>

          {/* Selected Items & Controls */}
          <div className="bg-white/60 backdrop-blur-md rounded-3xl p-6 border border-white/40">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-['Playfair_Display'] text-xl font-semibold text-[#1C1917]">
                Your Wheel ({selectedItems.length} items)
              </h4>
              {selectedItems.length > 0 && (
                <button
                  onClick={resetSelection}
                  className="text-[#1C1917]/60 hover:text-[#F97316] transition-colors"
                  data-testid="reset-selection"
                >
                  <RotateCcw className="w-5 h-5" />
                </button>
              )}
            </div>

            {selectedItems.length === 0 ? (
              <div className="h-[300px] flex items-center justify-center">
                <p className="text-[#1C1917]/50 font-['Outfit'] text-center">
                  Select at least 2 dishes from the left to build your custom wheel
                </p>
              </div>
            ) : (
              <ScrollArea className="h-[300px] pr-4">
                <div className="space-y-2">
                  {selectedItems.map((item, index) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 p-3 bg-gradient-to-r from-[#F97316]/10 to-[#FBBF24]/10 rounded-xl border border-[#F97316]/20"
                    >
                      <span className="w-6 h-6 rounded-full bg-[#F97316] text-white text-sm flex items-center justify-center font-semibold">
                        {index + 1}
                      </span>
                      <span className="font-['Outfit'] text-[#1C1917]">{item.name}</span>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="ml-auto text-[#1C1917]/40 hover:text-red-500 transition-colors"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}

            <div className="mt-6">
              <Button
                onClick={() => setShowWheel(true)}
                disabled={selectedItems.length < 2}
                data-testid="build-wheel-button"
                className="w-full bg-gradient-to-r from-[#F97316] to-[#FBBF24] hover:from-[#EA580C] hover:to-[#F59E0B] text-white font-semibold py-6 rounded-full text-lg shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {selectedItems.length < 2 
                  ? `Add ${2 - selectedItems.length} more dish${2 - selectedItems.length > 1 ? 'es' : ''}`
                  : 'Build My Wheel'
                }
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-8">
          <div className="flex gap-4 mb-4">
            <Button
              onClick={resetSelection}
              variant="outline"
              data-testid="edit-wheel-button"
              className="border-[#F97316] text-[#F97316] hover:bg-[#F97316] hover:text-white rounded-full px-6"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Edit Selection
            </Button>
          </div>
          <SpinnerWheel 
            items={selectedItems} 
            onSpinComplete={handleSpinComplete} 
            size={400} 
          />
          <p className="text-sm text-[#1C1917]/60 font-['Outfit'] text-center max-w-md">
            Your custom wheel with {selectedItems.length} dishes you selected!
          </p>
        </div>
      )}
    </div>
  );
};
